import {Store, createActions, FluxStoreBoundComponent} from 'rlink-flux';
import {Identity} from 'rlink-utils/functional';
import {camelToConstCase} from 'rlink-utils/string';

export let formActions = createActions({
  /**
   * Register a named form with optional default values.
   * @param {Object} payload
   * @param {string} payload.name
   * @param {Object} payload.initialValues
   */
  InitForm: Identity,
  UpdateFieldValue: Identity,
  CommitFieldValue: Identity,
  DiscardFieldChangeRecord: Identity,
  FieldWasValidated: Identity,
  DisableForm: payload => ({...payload, disabled: true}),
  EnableForm: payload => ({...payload, disabled: false}),
});

class FormsStore extends Store {
  constructor () {
    super();
    this.forms = {};
  }

  _registerForm (payload) {
    let fields = {};

    if (payload.initialValues) {
      for (let key in payload.initialValues) {
        fields[key] = {
          name: key,
          value: payload.initialValues[key],
          committedValue: payload.initialValues[key],
          changes: [],
        };
      }
    }

    let form = this.getFormState(payload.name);
    form.fields = fields;
  }

  getFormState (formName) {
    let form = this.forms[formName];

    if (!form) {
      form = this.forms[formName] = {
        name: formName,
        fields: {},
      };
    }

    return form;
  }

  getField (payload) {
    let form = this.getFormState(payload.form);
    let field = form.fields[payload.field];

    if (!field) {
      field = this.forms[payload.form].fields[payload.field] = {
        name: payload.field,
        changes: [],
      };
    }

    return field;
  }

  isInvalid (formName) {
    let form = this.getFormState(formName);

    for (let fieldName in form.fields) {
      let field = form.fields[fieldName];

      if ('valid' in field) {
        if (!field.valid) {
          return true;
        }
      }
    }

    return false;
  }

  onDispatch (dispatch) {
    let payload = dispatch.payload;
    let field, form;
    switch (dispatch.action) {
      case formActions.InitForm:
        this._registerForm(payload);
        this.notifyChanged(payload.afterAction);
        break;
      case formActions.UpdateFieldValue:
        this.getField(payload).value = payload.value;
        this.notifyChanged(payload.afterAction);
        break;
      case formActions.CommitFieldValue:
        field = this.getField(payload);
        field.committedValue = payload.value;
        field.changes = field.changes.concat(payload.changes);
        this.notifyChanged(payload.afterAction);
        break;
      case formActions.FieldWasValidated:
        field = this.getField(payload);
        field.validationProblems = payload.problems;
        field.valid = payload.valid;
        field.value = payload.value;
        this.notifyChanged(payload.afterAction);
        break;
      case formActions.DiscardFieldChangeRecord:
        form = this.getFormState(payload.form);

        if (payload.field) {
          form.fields[payload.field].changes = [];
        } else {
          for (let objField of Object.values(form.fields)) {
            objField.changes = [];
          }
        }

        this.notifyChanged(payload.afterAction);
        break;
      case formActions.EnableForm:
      case formActions.DisableForm:
        form = this.getFormState(payload.name);

        for (let objField of Object.values(form.fields)) {
          objField.disabled = payload.disabled;
        }

        this.notifyChanged(payload.afterAction);
        break;
    }
  }
}

export let formsStore = new FormsStore();

export class FormComponent extends FluxStoreBoundComponent {
  constructor (props) {
    super(props);
    this._inputProps = {};
    this.inputs = new Set();
  }

  get formName () {
    return this.props.formName;
  }

  componentWillUpdate () {
    // clear input props cache whenever we update for whatever reason
    this._inputProps = {};
  }


  getStore () {
    return formsStore;
  }

  getStateFromStore () {
    return formsStore.getFormState(this.formName);
  }

  getSubmitData () {
    let params = {};
    for (let name in this.state.fields) {
      params[name] = this.state.fields[name].value;
    }
    return params;
  }

  getFieldProblems (fieldName) {
    let fieldProblems = [];
    for (let problem in this.state.problems) {
      if (this.state.problems[problem].field === fieldName) {
        fieldProblems.push(this.getProblemMessage(this.state.problems[problem].type,
          fieldName));
      }
    }
    return fieldProblems;
  }

  getFormProblems () {
    let formProblems = [];
    for (let problem in this.state.problems) {
      if (this.state.problems[problem].field === undefined) {
        formProblems.push(this.getProblemMessage(this.state.problems[problem].type,
          null,
          this.state.problems[problem].messageParams));
      }
    }
    return formProblems;
  }

  getProblemMessage (problemType, fieldName, messageParams) {
    let messageKey = ['PROBLEM']
      .concat((fieldName === undefined || fieldName === null) ? [] : [camelToConstCase(fieldName)])
      .concat([problemType])
      .join('_');

    if (messageParams) {
      return this.props.loc.replaceParams(`PROBLEM_${problemType}`, messageParams, {target: '_blank'});
    }

    return this.props.loc[messageKey];
  }

  mapProblems (serviceProblems) {
    let problems = [];

    for (let problem in serviceProblems) {
      problems.push({
        type: serviceProblems[problem].type,
        field: serviceProblems[problem].param,
        messageParams: serviceProblems[problem].messageParams,
      });
    }

    return problems;
  }

  isValid () {
    for (let fieldName in this.state.fields) {
      if ('valid' in this.state.fields[fieldName]
          && this.state.fields[fieldName].valid === false) {
        return false;
      }
    }

    return true;
  }

  validationProblemsForField (field) {
    return field.validationProblems || [];
  }

  inputProps (fieldName) {
    if (!(fieldName in this._inputProps)) {
      let field = formsStore.getField({
        form: this.formName,
        field: fieldName,
      });

      this._inputProps[fieldName] = {
        ref: input => {
          if (input) {
            this.inputs.add(input);
          } else {
            this.inputs = new Set(Array.from(this.inputs).filter(
              existingInput => !existingInput._inputWillUnmount));
          }
        },
        fieldName,
        value: field.value,
        committedValue: field.committedValue,
        loc: this.props.loc,
        disabled: field.disabled,
        changes: field.changes,
        validationProblems: this.validationProblemsForField(field),
        onChange: ev => {
          let value;

          if ('value' in ev) {
            value = ev.value;
          } else {
            value = ev.target.value;
          }

          formActions.UpdateFieldValue({
            form: this.formName,
            field: fieldName,
            value,
          });
        },
        onChangeDidCommit: ev => {
          formActions.CommitFieldValue({
            form: this.formName,
            field: fieldName,
            value: ev.value,
            rawValue: ev.rawValue,
            changes: ev.changes,
          });
        },
        onValidate: ev => {
          formActions.FieldWasValidated({
            form: this.formName,
            field: fieldName,
            value: ev.value,
            problems: ev.problems,
            valid: ev.valid,
          });
        },
      };
    }
    return this._inputProps[fieldName];
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-forms/flux.js